import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useCreateGlobalPcSetMutation } from '@/entities/pricing-campaign'

import { FormProvider, RHFTextField } from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { queryClient } from '@/shared/lib/query-client'
import { zodResolver } from '@/shared/lib/validator/zod'

export function CreateGlobalPcSetModal({
	isOpen,
	close,
}: {
	isOpen: boolean
	close: () => void
}) {
	const { t } = useTranslation()

	const methods = useForm({
		defaultValues: {
			name: '',
		},
		resolver: zodResolver(
			z.object({
				name: z.string().min(5, t('validation.min_length', { val: 5 })),
			})
		),
	})
	const createGlobalPcSetMutation = useCreateGlobalPcSetMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['global-pc-sets-list'],
			})
		},
	})
	const _handleSubmit = async ({ name }: { name: string }) => {
		await toast.promise(createGlobalPcSetMutation.mutateAsync({ name }), {
			loading: t('global_set.create.processing.loading', {
				defaultValue: 'Creating global set...',
			}),
			success: t('global_set.create.processing.success', {
				defaultValue: 'Global set created successfully',
			}),
			error: t('global_set.create.processing.error', {
				defaultValue: 'Error while creating global set',
			}),
		})
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent>
				<ModalHeader>
					<ModalTitle>
						{t('global_set.create.modal.title', {
							defaultValue: 'Create new global set',
						})}
					</ModalTitle>
				</ModalHeader>
				<ModalBody className='flex flex-col gap-4'>
					{createGlobalPcSetMutation.error?.response?.status === 422 && (
						<Alert variant='destructive'>
							<AlertDescription>{t('fatal_error_title')}</AlertDescription>
						</Alert>
					)}

					<FormProvider
						methods={methods}
						onSubmit={handleSubmit}
						className='flex flex-col gap-4'
					>
						<RHFTextField
							className='w-full'
							autoComplete='off'
							autoFocus
							disabled={createGlobalPcSetMutation.isPending}
							placeholder={t('global_set.name.placeholder', {
								defaultValue: 'e.g. Main global set',
							})}
							name='name'
							required
						/>
					</FormProvider>
				</ModalBody>
				<ModalFooter className='justify-between'>
					<ModalClose asChild>
						<Button>{t('general_cancel')}</Button>
					</ModalClose>
					<Button
						variant='primary'
						disabled={
							createGlobalPcSetMutation.isPending || !methods.watch('name')
						}
						onClick={handleSubmit}
					>
						{createGlobalPcSetMutation.isPending && <Spinner />}
						{t('general_create')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
